import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { StaffRole } from '../../types/user';
import { useGymAdmin } from '../context/gym-admin-context';
import { allowedNavItems, isNavPathAllowed, type ManagedLocationType, type PlaceShape } from '../nav';

/** Path a user should land on given their roles (first allowed menu entry). */
function firstAllowedPath(
  roles: StaffRole[],
  locationType: ManagedLocationType,
  place: PlaceShape
): string {
  return allowedNavItems(roles, locationType, place)[0]?.path ?? '/no-access';
}

/**
 * Redirects to the first section the user's roles allow (or the no-access page)
 * when the wrapped page is not theirs to open — because they lack every role it
 * asks for, or because it does not exist for this kind of place. Used both to
 * guard pages and to resolve the default landing route.
 *
 * The second test is what catches switching from a gym to a crag while standing
 * on, say, the hours page: the roles carry over, the section does not.
 */
export function RequireRoles({
  roles,
  path,
  children,
}: {
  roles: StaffRole[];
  /** The entry's own path, when it is one the location type may not have. */
  path?: string;
  children: ReactNode;
}) {
  const { hasAnyRole, roles: userRoles, locationType, place, placePath } = useGymAdmin();

  const availableHere = path === undefined || isNavPathAllowed(path, userRoles, locationType, place);

  if (!hasAnyRole(roles) || !availableHere) {
    return <Navigate to={placePath(firstAllowedPath(userRoles, locationType, place))} replace />;
  }

  return <>{children}</>;
}

/** Sends the user to the first section their roles allow, at the active place. */
export function DefaultRedirect() {
  const { roles, locationType, place, placePath } = useGymAdmin();
  return <Navigate to={placePath(firstAllowedPath(roles, locationType, place))} replace />;
}
